import { FC, forwardRef, useEffect } from 'react';

import { observer } from 'mobx-react';
import { useParams, ParamParseKey } from 'react-router-dom';
import InputMask from 'react-input-mask';

import { useDependencies } from '@servicetitan/react-ioc';
import { CenteredSpinner } from '@servicetitan/marketing-ui';
import { Flex, TextField, Textarea, TextFieldProps } from '@servicetitan/anvil2';
import { Mask } from '@servicetitan/design-system';

import { TenantSmsConfigurationStore } from '../../../stores/tenant-sms-configuration.store';
import { NotificationsStore } from '../../../../common/stores/notifications.store';
import { LoadStatus } from '../../../../common/enums/load-status';
import { AccountManagementRoutes } from '../../../utils/routes';

export const Sms: FC = observer(() => {
    const { externalTenantId, externalPartnerId } =
        useParams<ParamParseKey<typeof AccountManagementRoutes.manageTenant>>();

    const [
        { init, loadStatus, formState, saveLoadStatus, resetConfiguration },
        { notifyInSnackbar },
    ] = useDependencies(TenantSmsConfigurationStore, NotificationsStore);

    useEffect(() => {
        if (externalTenantId && externalPartnerId) {
            init();
        }
    }, [init, externalTenantId, externalPartnerId]);

    useEffect(() => {
        if (saveLoadStatus === LoadStatus.Ok) {
            notifyInSnackbar({ title: 'Changes saved' });
        }
    }, [saveLoadStatus, notifyInSnackbar]);

    useEffect(
        () => () => {
            resetConfiguration();
        },
        [resetConfiguration],
    );

    if (loadStatus === LoadStatus.Loading) {
        return <CenteredSpinner />;
    }

    if (loadStatus === LoadStatus.Error) {
        return <div>Failed to load SMS configuration</div>;
    }

    const {
        marketingTcrCampaignCallToActionMessageId,
        marketingTcrCampaignHelpMessageId,
        marketingTcrCampaignMessageFlowId,
        marketingTcrCampaignOptInConfirmationMessageId,
        marketingTcrCampaignOptInMessageId,
        marketingTcrCampaignOptOutMessageId,
        customMarketingSmsOptInMessage,
    } = formState.$;

    const isSaving = saveLoadStatus === LoadStatus.Loading;

    return (
        <Mask active={isSaving}>
            <Flex direction="column" lg={{ direction: 'row' }} gap={8}>
                <Flex direction="column" grow={1} basis={0} gap={6}>
                    <MaskedIdField
                        label="MarketingTcrCampaignCallToActionMessageId"
                        value={marketingTcrCampaignCallToActionMessageId.value ?? undefined}
                        onChange={marketingTcrCampaignCallToActionMessageId.onChangeNativeHandler}
                    />
                    <MaskedIdField
                        label="MarketingTcrCampaignHelpMessageId"
                        value={marketingTcrCampaignHelpMessageId.value ?? undefined}
                        onChange={marketingTcrCampaignHelpMessageId.onChangeNativeHandler}
                    />
                    <MaskedIdField
                        label="MarketingTcrCampaignMessageFlowId"
                        value={marketingTcrCampaignMessageFlowId.value ?? undefined}
                        onChange={marketingTcrCampaignMessageFlowId.onChangeNativeHandler}
                    />
                    <MaskedIdField
                        label="MarketingTcrCampaignOptInConfirmationMessageId"
                        value={marketingTcrCampaignOptInConfirmationMessageId.value ?? undefined}
                        onChange={
                            marketingTcrCampaignOptInConfirmationMessageId.onChangeNativeHandler
                        }
                    />
                    <MaskedIdField
                        label="MarketingTcrCampaignOptInMessageId"
                        value={marketingTcrCampaignOptInMessageId.value ?? undefined}
                        onChange={marketingTcrCampaignOptInMessageId.onChangeNativeHandler}
                    />
                    <MaskedIdField
                        label="MarketingTcrCampaignOptOutMessageId"
                        value={marketingTcrCampaignOptOutMessageId.value ?? undefined}
                        onChange={marketingTcrCampaignOptOutMessageId.onChangeNativeHandler}
                    />
                </Flex>
                <Flex direction="column" grow={1} basis={0} gap={4}>
                    <Textarea
                        label="Custom SMS Opt In Message"
                        rows={12}
                        value={customMarketingSmsOptInMessage.value}
                        onChange={e =>
                            customMarketingSmsOptInMessage.onChange(e?.target.value ?? '')
                        }
                    />
                </Flex>
            </Flex>
        </Mask>
    );
});

export const MaskedIdField = forwardRef<HTMLInputElement, TextFieldProps>(
    ({ ...textFieldProps }, ref) => {
        return (
            <InputMask
                mask="9999999999"
                maskChar=""
                alwaysShowMask
                value={textFieldProps.value}
                onChange={textFieldProps.onChange}
            >
                {(inputProps: any) => (
                    <TextField
                        ref={ref}
                        {...textFieldProps}
                        {...inputProps}
                        error={textFieldProps.error}
                        label={textFieldProps.label}
                    />
                )}
            </InputMask>
        );
    },
);
